import { debugLog } from './matchmakingConfig.js';
import {
  getUnitSize,
  getTotalPlayerCount,
  expandUnitsToPlayers,
  UNIT_TYPES,
} from './unitBuilder.js';

/**
 * Generate combinations of units that add up to exactly targetPlayerCount players
 * @param {Object[]} units - Array of units
 * @param {number} targetPlayerCount - Target number of players (usually 4)
 * @returns {Object[][]} Array of unit combinations
 */
function generateUnitCombinations(units, targetPlayerCount) {
  if (targetPlayerCount === 0) return [[]];
  if (units.length === 0) return [];
  
  const result = [];
  
  function backtrack(currentCombo, remainingUnits, currentPlayerCount) {
    if (currentPlayerCount === targetPlayerCount) {
      result.push([...currentCombo]);
      return;
    }
    
    if (currentPlayerCount > targetPlayerCount || remainingUnits.length === 0) {
      return;
    }
    
    for (let i = 0; i < remainingUnits.length; i++) {
      const unit = remainingUnits[i];
      const unitSize = getUnitSize(unit);
      const newPlayerCount = currentPlayerCount + unitSize;
      
      if (newPlayerCount <= targetPlayerCount) {
        currentCombo.push(unit);
        backtrack(currentCombo, remainingUnits.slice(i + 1), newPlayerCount);
        currentCombo.pop();
      }
    }
  }
  
  backtrack([], units, 0);
  return result;
}

// Keep old function for backward compatibility (used in tests)
function generateCombinations(arr, n) {
  if (n === 0) return [[]];
  if (n > arr.length) return [];
  const result = [];
  for (let i = 0; i <= arr.length - n; i++) {
    const head = arr[i];
    const tailCombinations = generateCombinations(arr.slice(i + 1), n - 1);
    tailCombinations.forEach(combo => {
      result.push([head, ...combo]);
    });
  }
  return result;
}

function generateTeamSplits(fourPlayerIds) {
  const [p1, p2, p3, p4] = fourPlayerIds;
  return [
    { team1: [p1, p2], team2: [p3, p4] },
    { team1: [p1, p3], team2: [p2, p4] },
    { team1: [p1, p4], team2: [p2, p3] },
  ];
}

/**
 * Generate team splits for 4 players, ensuring partnerships are on the same team
 * Since partnerships are now units, when we expand to 4 players, partners are guaranteed to be together.
 * This function ensures they're on the same team in the split.
 * @param {string[]} fourPlayerIds - Array of 4 player IDs
 * @param {Array} partnerships - Array of partnership objects (for validation)
 * @returns {Object[]} Array of valid team splits
 */
function generateTeamSplitsRespectingPartnerships(fourPlayerIds, partnerships = []) {
  const [p1, p2, p3, p4] = fourPlayerIds;
  const allSplits = [
    { team1: [p1, p2], team2: [p3, p4] },
    { team1: [p1, p3], team2: [p2, p4] },
    { team1: [p1, p4], team2: [p2, p3] },
  ];

  // If no partnerships, return all splits
  if (!partnerships || partnerships.length === 0) {
    return allSplits;
  }

  // Build partnership map for quick lookup
  const partnershipMap = new Map();
  partnerships.forEach(p => {
    const partner1 = p.player1Id || p.player1_id;
    const partner2 = p.player2Id || p.player2_id;
    partnershipMap.set(partner1, partner2);
    partnershipMap.set(partner2, partner1);
  });

  // Check if any partnerships exist in these 4 players
  const playersInPartnerships = new Set();
  fourPlayerIds.forEach(id => {
    if (partnershipMap.has(id)) {
      const partnerId = partnershipMap.get(id);
      if (fourPlayerIds.includes(partnerId)) {
        playersInPartnerships.add(id);
        playersInPartnerships.add(partnerId);
      }
    }
  });

  // If no partnerships in this group, return all splits
  if (playersInPartnerships.size === 0) {
    return allSplits;
  }

  // Filter splits to only include those where partners are on the same team
  // Since partnerships are units, both partners are guaranteed to be in the 4 players
  const validSplits = allSplits.filter(split => {
    // Check each partnership
    for (const [partner1, partner2] of partnershipMap.entries()) {
      // Only check partnerships where both players are in this game
      if (!fourPlayerIds.includes(partner1) || !fourPlayerIds.includes(partner2)) {
        continue;
      }

      const partner1InTeam1 = split.team1.includes(partner1);
      const partner2InTeam1 = split.team1.includes(partner2);
      const partner1InTeam2 = split.team2.includes(partner1);
      const partner2InTeam2 = split.team2.includes(partner2);

      // Partners must be on the same team
      const onSameTeam = (partner1InTeam1 && partner2InTeam1) || (partner1InTeam2 && partner2InTeam2);
      if (!onSameTeam) {
        debugLog(`🚫 Filtering split: Partnership violation - ${partner1} and ${partner2} on opposite teams`);
        return false; // This split violates the partnership
      }
    }
    return true; // All partnerships are respected in this split
  });

  if (validSplits.length < allSplits.length) {
    debugLog(`🤝 generateTeamSplitsRespectingPartnerships: Filtered ${allSplits.length - validSplits.length} splits due to partnership violations`);
  }

  return validSplits.length > 0 ? validSplits : allSplits; // Fallback to all splits if none are valid (shouldn't happen)
}

/**
 * Generate combinations of units from two pools (A and B) that add up to playersPerMatch players
 * @param {Object[]} poolA - Array of units from pool A
 * @param {Object[]} poolB - Array of units from pool B
 * @param {number} playersFromA - Target number of players from pool A
 * @param {number} [playersPerMatch=4] - Total players per game (4 doubles, 2 singles)
 * @returns {Object[][]} Array of unit combinations (each combination is an array of units)
 */
function generatePoolCombinations(poolA, poolB, playersFromA, playersPerMatch = 4) {
  const ppm = Math.max(2, playersPerMatch);
  const targetPlayersFromA = Math.min(playersFromA, ppm);
  const targetPlayersFromB = ppm - targetPlayersFromA;
  
  // Generate unit combinations from pool A that add up to targetPlayersFromA
  const combosFromA = targetPlayersFromA > 0 
    ? generateUnitCombinations(poolA, targetPlayersFromA)
    : [[]];
  
  // Generate unit combinations from pool B that add up to targetPlayersFromB
  const combosFromB = targetPlayersFromB > 0
    ? generateUnitCombinations(poolB, targetPlayersFromB)
    : [[]];
  
  // Combine unit combinations from both pools
  const combined = [];
  combosFromA.forEach(aCombo => {
    combosFromB.forEach(bCombo => {
      const merged = [...aCombo, ...bCombo];
      const totalPlayers = getTotalPlayerCount(merged);
      if (totalPlayers === ppm) {
        combined.push(merged);
      }
    });
  });
  
  debugLog('🔧 generatePoolCombinations', {
    poolAUnits: poolA.length,
    poolBUnits: poolB.length,
    targetPlayersFromA,
    targetPlayersFromB,
    playersPerMatch: ppm,
    generated: combined.length,
  });
  
  return combined;
}

export {
  generateCombinations,
  generateUnitCombinations,
  generatePoolCombinations,
  generateTeamSplits,
  generateTeamSplitsRespectingPartnerships,
};

